import { ROUTES, callAPI } from "../API/index";
import { renameKeys } from "./ServiceCommon";
import axiosInstance from "../axiosInstance";

// -----------------------------------------------
// Management
// -----------------------------------------------
export const fetchAllCompany = () => {
    /**
     * 회사 목록 반환
     */
    return callAPI(ROUTES.MANAGEMENT.CORPORATIONS, 'GET');
}

export const fetchCompanyMenuItems = async () => {
    /**
     * 회사 목록 MenuItems 형태로 반환
     */
    try {
        const data = await callAPI(ROUTES.MANAGEMENT.CORPORATIONS, 'GET'); // API 호출
        const menuItemKeyMap = { companyNo: ['key', 'value'], companyName: 'text' };
        const menuItems = data.map(item => renameKeys(item, menuItemKeyMap))

        return menuItems
    } catch (error) {
        console.error("회사 목록 가져오기 오류:", error);
        throw error; // 오류를 다시 던져서 호출하는 쪽에서 처리할 수 있도록 함
    }
}

export const fetchCompany = (corp_no) => {
    /**
     * 회사 조회
     */
    return callAPI(ROUTES.MANAGEMENT.CORPORATION, 'GET', {}, { corp_no });
}

export const fetchCompanyName = async (corp_no) => {
    /**
     * 회사 이름 조회
     */
    const resp = await callAPI(ROUTES.MANAGEMENT.CORPORATION, 'GET', {}, { corp_no });
    if (resp.companyName) {
        return resp.companyName
    } else {
        return '회사명을 찾지 못함'
    }
}

export const createCompany = (formData) => {
    /**
     * 회사 생성
     */
    // 날짜 필드 전처리
    const processDateField = (dateValue) => {
        if (!dateValue) return undefined;
        if (typeof dateValue === 'string') return dateValue;
        if (dateValue instanceof Date) return dateValue.toISOString().split('T')[0];
        return undefined;
    };

    // 숫자 필드 전처리
    const processNumberField = (numberValue) => {
        if (numberValue === null || numberValue === undefined || numberValue === '') return undefined;
        return parseInt(numberValue) || undefined;
    };

    const transformedData = {
        // 기본적으로 모든 formData 전달
        ...formData,
        
        // 특별한 전처리가 필요한 필드들만 오버라이드
        companyBusinessScale: processNumberField(formData.companyBusinessScale),
        companyBaseTime: processNumberField(formData.companyBaseTime),
        
        // 날짜 필드 처리
        contractStrDate: processDateField(formData.contractStrDate),
        contractEndDate: processDateField(formData.contractEndDate),
        contractPaymentDate: processDateField(formData.contractPaymentDate),
        
        // 빈 문자열을 undefined로 변환
        managerPassword: formData.managerPassword || undefined,
        companyRemark: formData.companyRemark || undefined,
    };

    // undefined 값 제거
    const filteredData = Object.fromEntries(
        Object.entries(transformedData).filter(([_, v]) => v !== undefined)
    );

    return callAPI(ROUTES.MANAGEMENT.CORPORATIONS, 'POST', filteredData);
}

export const updateCompany = (formData, corp_no) => {
    // 날짜 필드 전처리
    const processDateField = (dateValue) => {
        if (!dateValue) return undefined;
        if (typeof dateValue === 'string') return dateValue;
        if (dateValue instanceof Date) return dateValue.toISOString().split('T')[0];
        return undefined;
    };

    // 숫자 필드 전처리
    const processNumberField = (numberValue) => {
        if (numberValue === null || numberValue === undefined || numberValue === '') return undefined;
        return parseInt(numberValue) || undefined;
    };

    // 로고 파일이 있는지 확인
    const hasLogoFile = formData.logoFile instanceof File;

    // 파일이 있는 경우 FormData 사용
    if (hasLogoFile) {
        const formDataToSend = new FormData();
        
        // 모든 필드를 FormData에 추가
        Object.entries(formData).forEach(([key, value]) => {
            // File 객체는 그대로 추가
            if (value instanceof File) {
                formDataToSend.append(key, value);
            }
            // undefined가 아닌 값만 추가
            else if (value !== undefined && value !== null && value !== '') {
                formDataToSend.append(key, value);
            }
        });

        // 날짜 필드 처리
        if (formData.contractDate) {
            formDataToSend.set('contractDate', processDateField(formData.contractDate));
        }
        if (formData.contractEndDate) {
            formDataToSend.set('contractEndDate', processDateField(formData.contractEndDate));
        }
        if (formData.contractPayday) {
            formDataToSend.set('contractPayday', processDateField(formData.contractPayday));
        }
        if (formData.contractStrDate) {
            formDataToSend.set('contractStrDate', processDateField(formData.contractStrDate));
        }
        if (formData.contractPaymentDate) {
            formDataToSend.set('contractPaymentDate', processDateField(formData.contractPaymentDate));
        }

        // 숫자 필드 처리
        if (formData.companyScale) {
            formDataToSend.set('companyScale', processNumberField(formData.companyScale));
        }
        if (formData.companyBaseTime) {
            formDataToSend.set('companyBaseTime', processNumberField(formData.companyBaseTime));
        }

        // axiosInstance를 사용하여 multipart/form-data로 전송
        return callAPI(
            ROUTES.MANAGEMENT.CORPORATION,
            'PUT',
            formDataToSend,
            { corp_no }
        );
    }

    // 파일이 없는 경우 기존 로직 사용
    const transformedData = {
        // 기본적으로 모든 formData 전달
        ...formData,
        
        // 특별한 전처리가 필요한 필드들만 오버라이드
        companyScale: processNumberField(formData.companyScale),
        companyBaseTime: processNumberField(formData.companyBaseTime),
        
        // 날짜 필드 처리
        contractDate: processDateField(formData.contractDate),
        contractEndDate: processDateField(formData.contractEndDate),
        contractPayday: processDateField(formData.contractPayday),
        contractStrDate: processDateField(formData.contractStrDate),
        contractPaymentDate: processDateField(formData.contractPaymentDate),
        
        // 빈 문자열을 undefined로 변환
        managerPassword: formData.managerPassword || undefined,
        companyRemark: formData.companyRemark || undefined,
    };

    // undefined 값 제거
    const filteredData = Object.fromEntries(
        Object.entries(transformedData).filter(([_, v]) => v !== undefined)
    );

    return callAPI(ROUTES.MANAGEMENT.CORPORATION, 'PUT', filteredData, { corp_no });
}

/** grp_type: 1=부서, 2=직위, 3=근태, 4=급여 */
export const GRP_TYPE = { DEPARTMENT: 1, POSITION: 2, ATTENDANCE: 3, SALARY: 4 };

export const fetchAllGRPInfo = async (grp_type) => {
    /**
     * 기초 그룹 목록 반환
     * @param {number} grp_type - 1:부서, 2:직위, 3:근태, 4:급여 (선택)
     */
    const params = grp_type != null ? { grp_type } : {};
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO, 'GET', {}, params);
}

export const fetchAllGRPInfoMenuItems = async () => {
    /**
     * 기초그룹 목록 MenuItems 형태로 만들어서 반환(Select에 사용)
     * 확실한 resp 목록 반환
     */
    try {
        const data = await callAPI(ROUTES.MANAGEMENT.BASIC_INFO, 'GET');
        
        const menuItemKeyMap = { no: ['key', 'value'], grpType: 'grp_type', grpNm: 'text' };
        const menuItems = data.map(item => renameKeys(item, menuItemKeyMap))

        return menuItems
    } catch (error) {
        console.error("기초그룹 목록 가져오기 오류:", error);
        throw error; // 오류를 다시 던져서 호출하는 쪽에서 처리할 수 있도록 함
    }
}

export const fetchBasicPositionList = async (grp_no) => {
    /**
     * 기초 직위 목록 반환
     * Django URL:
     *   path("management/basic-info/company-info/position/", BasicInfoOGDPs.as_view())
     * - grp_no는 쿼리 파라미터로 전달
     */
    const url = '/management/basic-info/company-info/position/';
    const params = grp_no != null ? { grp_no } : {};
    try {
        const resp = await axiosInstance.get(url, { params });
        return resp.data;
    } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '관리 서비스 처리 중 오류가 발생했습니다.'));
    }
}

export const fetchBasicPositionMenuItems = async (grp_no) => {
    /**
    * 기초 그룹 목록 중 직책 MenuItems 형태로 만들어서 반환(Select에 사용)
    * 확실한 resp 목록 반환
    */
    try {
        const data = await fetchBasicPositionList(grp_no); // API 호출
        const menuItemKeyMap = { no: ['key', 'value'], boglVal: 'text' };
        const menuItems = data.map(item => renameKeys(item, menuItemKeyMap))

        return menuItems
    } catch (error) {
        console.error("직위 목록 가져오기 오류:", error);
        throw error; // 오류를 다시 던져서 호출하는 쪽에서 처리할 수 있도록 함
    }
}

export const fetchBasicDepartmentList = async (grp_no) => {
    /**
     * 기초 부서 목록 반환
     * Django URL:
     *   path("management/basic-info/company-info/department/", BasicInfoOGDPs.as_view())
     * - grp_no는 쿼리 파라미터로 전달
     */
    const url = '/management/basic-info/company-info/department/';
    const params = grp_no != null ? { grp_no } : {};
    try {
        const resp = await axiosInstance.get(url, { params });
        return resp.data;
    } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '관리 서비스 처리 중 오류가 발생했습니다.'));
    }
}

export const fetchBasicDepartmentMenuItems = async (grp_no) => {
    /**
    * 기초 그룹 목록 중 부서 MenuItems 형태로 만들어서 반환(Select에 사용)
    * 확실한 resp 목록 반환
    */
    try {
        const data = await fetchBasicDepartmentList(grp_no); // API 호출
        const menuItemKeyMap = { no: ['key', 'value'], boglVal: 'text' };
        const menuItems = data.map(item => renameKeys(item, menuItemKeyMap))

        return menuItems
    } catch (error) {
        console.error("부서 목록 가져오기 오류:", error);
        throw error; // 오류를 다시 던져서 호출하는 쪽에서 처리할 수 있도록 함
    }
}

// -----------------------------------------------
// 기초 그룹 / 부서 / 직위 추가·수정·삭제
// -----------------------------------------------
const BASIC_INFO_BASE = '/management/basic-info';
const COMPANY_INFO_BASE = `${BASIC_INFO_BASE}/company-info`;

export const createBasicGroup = async (apiData) => {
    try {
        const resp = await axiosInstance.post(`${BASIC_INFO_BASE}/`, apiData);
        return resp.data;
    } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '기초 그룹 생성 중 오류가 발생했습니다.'));
    }
};

export const updateBasicGroup = async (grp_no, apiData) => {
    try {
        const resp = await axiosInstance.put(`${BASIC_INFO_BASE}/${grp_no}/`, apiData);
        return resp.data;
    } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '기초 그룹 수정 중 오류가 발생했습니다.'));
    }
};

export const createBasicDepartment = async (apiData) => {
    try {
        const resp = await axiosInstance.post(`${COMPANY_INFO_BASE}/department/`, apiData);
        return resp.data;
    } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '기초 부서 생성 중 오류가 발생했습니다.'));
    }
};

export const updateBasicDepartment = async (apiData) => {
    try {
        const resp = await axiosInstance.put(`${COMPANY_INFO_BASE}/department/`, apiData);
        return resp.data;
    } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '기초 부서 수정 중 오류가 발생했습니다.'));
    }
};

export const deleteBasicDepartment = async (apiData) => {
    try {
        const resp = await axiosInstance.delete(`${COMPANY_INFO_BASE}/department/`, { data: apiData });
        return resp.data;
    } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '기초 부서 삭제 중 오류가 발생했습니다.'));
    }
};

export const createBasicPosition = async (apiData) => {
    try {
        const resp = await axiosInstance.post(`${COMPANY_INFO_BASE}/position/`, apiData);
        return resp.data;
    } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '기초 직위 생성 중 오류가 발생했습니다.'));
    }
};

export const updateBasicPosition = async (apiData) => {
    try {
        const resp = await axiosInstance.put(`${COMPANY_INFO_BASE}/position/`, apiData);
        return resp.data;
    } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '기초 직위 수정 중 오류가 발생했습니다.'));
    }
};

export const deleteBasicPosition = async (apiData) => {
    try {
        const resp = await axiosInstance.delete(`${COMPANY_INFO_BASE}/position/`, { data: apiData });
        return resp.data;
    } catch (error) {
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '기초 직위 삭제 중 오류가 발생했습니다.'));
    }
};

export const fetchBasicInsuranceList = () => {
    /**
     * 기초 보험 목록 반환
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.INSURANCE, 'GET');
}

export const createBasicInsurance = (apiData) => {
    /**
     * 기초 보험(4대보험 요율) 단건 등록
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.INSURANCE, 'POST', apiData);
}

export const createBasicInsuranceList = (list) => {
    /**
     * 기초 보험(4대보험 요율) 일괄 등록 - 리스트로 전송
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.INSURANCE, 'POST', list);
}

export const updateBasicInsurance = (apiData) => {
    /**
     * 기초 보험(4대보험 요율) 수정
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.INSURANCE, 'PUT', apiData);
}

export const deleteBasicInsurance = (apiData) => {
    /**
     * 기초 보험(4대보험 요율) 삭제 - { no } 또는 { nos: number[] }
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.INSURANCE, 'DELETE', apiData);
}

export const fetchBasicTaxList = () => {
    /**
     * 기초 소득세율 목록 반환
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.TAX, 'GET');
}

export const fetchBasicSimpleTaxList = () => {
    /**
     * 기초 간이세액표 목록 반환
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.SIMPLE_TAX, 'GET');
}

export const uploadBasicSimpleTaxExcel = (formData) => {
    /**
     * 간이세액표 엑셀 파일 등록
     * @param {FormData} formData - stdYear(연도), file(엑셀 파일) 포함. 응답에 std_year 포함.
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.SIMPLE_TAX.EXCEL, 'POST', formData);
}

export const fetchBasicAttendanceList = () => {
    /**
     * 기초 근태 목록 반환
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.ATTENDANCE, 'GET');
}

export const createBasicAttendance = (apiData) => {
    /**
     * 기초 근태 생성
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.ATTENDANCE, 'POST', apiData);
}

export const updateBasicAttendanceList = (apiData) => {
    /**
     * 기초 근태 수정
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.ATTENDANCE, 'PUT', apiData);
}

export const deleteBasicAttendance = (apiData) => {
    /**
     * 기초 근태 삭제
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.ATTENDANCE, 'DELETE', apiData);
}

export const fetchBasicSalaryList = () => {
    /**
     * 기초 급여 목록 반환
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.SALARY, 'GET');
}

export const fetchBasicHolidayList = () => {
    /**
     * 기초 공휴일 목록 반환
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.HOLIDAY, 'GET');
}

export const fetchBasicHolidayPreview = (year, month) => {
    /**
     * 연도별 기초 공휴일 미리보기 (추가 대상 휴일 조회)
     * @param {string} year - 4자리 연도 (예: 2025)
     * @param {number} [month] - 1~12 (있으면 해당 월만, 없으면 해당 연도 전체)
     */
    const params = { year };
    if (month != null && month >= 1 && month <= 12) {
        params.month = month;
    }
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.HOLIDAY.PREVIEW, 'GET', {}, params);
}

export const addBasicHolidays = (holidayList) => {
    /**
     * 기초 공휴일 목록 일괄 추가
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.HOLIDAY, 'POST', { holidayList });
}

export const updateBasicHoliday = (apiData) => {
    /**
     * 기초 공휴일 수정
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.HOLIDAY, 'PUT', apiData);
}

export const deleteBasicHolidays = (nos) => {
    /**
     * 기초 공휴일 삭제 (no 배열)
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.HOLIDAY, 'DELETE', { nos });
}

export const applyBasicHolidaysToCompanies = (year, corpNos) => {
    /**
     * 선택한 연도의 기초 공휴일을 선택한 회사들에 적용
     * @param {string} year - 4자리 연도 (예: 2025)
     * @param {string[]} corpNos - 회사 번호 목록
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.HOLIDAY.APPLY_TO_COMPANIES, 'POST', { year, corpNos });
}

export const fetchManagerList = () => {
    /**
     * 관리자 목록 반환
     */
    return callAPI(ROUTES.MANAGEMENT.MANAGER, 'GET');
}

export const fetchBasicInsuranceApplyList = () => {
    /**
     * 기초 보험 적용 목록 반환
     */
    return callAPI(ROUTES.MANAGEMENT.BASIC_INFO.INSURANCE.CURRENT, 'GET');
}

export const fetchLoginHistory = () => {
    /**
     * 로그인 기록 조회
     */
    return callAPI(ROUTES.MANAGEMENT.LOGIN_HISTORY, 'GET');
}
